import { Box, Container, Grid } from "@mui/material"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import CheckBoxGroupField from "src/components/atom/CheckBoxGroupField"

import { Dayjs } from "dayjs"
import { useMediaQuery } from "react-responsive"
import { useRecoilState } from "recoil"
import InputPeriodField from "src/components/atom/InputPeriodField"
import { OutlinedButton } from "src/components/buttons/OutlinedButton"
import { Title } from "src/components/layout/Title"
import { useLoginContext } from "src/hooks/useLoginContext"
import { EVENT_STATUS, RESPONSE_CODE, SNACKBAR_TYPES, SORT_ORDER } from "src/modules/const"
import { Event, EventService } from "src/openapi"
import { ShowToast } from "src/states/showToast"
import "src/styles/common.scss"
import { Button, Typography } from "src/UILibrary"
import { EventArchiveItem } from "./components/EventArchiveItem"
import { EventItem } from "./components/EventItem"
import { EventJoinModal } from "./components/EventJoinModal"

const PAGE_SIZE = 5
const ARCHIVE_PAGE_SIZE = 9

export const EventPage = () => {
  const { t } = useTranslation("translation", { keyPrefix: "event" })

  const isPC = useMediaQuery({ query: "(min-width: 1200px)" })

  const { initialized, areas, eventCategories, accessToken } = useLoginContext()

  const [, setShowToast] = useRecoilState(ShowToast)

  const [areaID, setAreaID] = useState<number[]>([])
  const [online, setOnline] = useState<string[]>([])
  const [fromDate, setFromDate] = useState<Dayjs | null>(null)
  const [toDate, setToDate] = useState<Dayjs | null>(null)
  const [categories, setCategories] = useState<number[]>([])

  const [openJoinModal, setOpenJoinModal] = useState<boolean>(false)
  const [selectedItem, setSelectedItem] = useState<Event>()

  const [events, setEvents] = useState<Event[]>([])
  const [archiveEvents, setArchiveEvents] = useState<Event[]>([])

  const [total, setTotal] = useState<number>(0)
  const [archiveTotal, setArchiveTotal] = useState<number>(0)

  const handleItemSelected = (item: Event) => {
    setSelectedItem(item)
    setOpenJoinModal(true)
  }

  const handleGetEvents = (reset?: boolean) => {
    EventService.listEvent(
      undefined,
      reset ? 0 : events.length,
      PAGE_SIZE,
      areaID.join(","),
      categories.join(","),
      undefined,
      online.join(","),
      [EVENT_STATUS.RECEIVING, EVENT_STATUS.STARTED].join(","),
      (fromDate && fromDate.isValid()) || (toDate && toDate.isValid())
        ? `${fromDate && fromDate.isValid() ? fromDate?.format("YYYY-MM-DD") : ""},${
            toDate && toDate.isValid() ? toDate?.format("YYYY-MM-DD") : ""
          }`
        : undefined,
      SORT_ORDER.ASCENDING,
      "start_time"
    )
      .then((res) => {
        if (res.status === RESPONSE_CODE.SUCCESS) {
          if (reset) {
            setEvents(res.data?.events as Event[])
          } else {
            setEvents([...events, ...(res.data?.events as Event[])])
          }

          setTotal(res.data?.count as number)
        } else {
          setShowToast({ show: true, text: t("failed_list"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowToast({ show: true, text: t("failed_list"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handleGetArchiveEvents = (reset?: boolean) => {
    EventService.listEvent(
      accessToken,
      archiveEvents.length,
      ARCHIVE_PAGE_SIZE,
      undefined,
      undefined,
      undefined,
      undefined,
      EVENT_STATUS.ARCHIVED,
      undefined,
      SORT_ORDER.DESCENDONG,
      "start_time"
    )
      .then((res) => {
        if (res.status === RESPONSE_CODE.SUCCESS) {
          if (reset) {
            setArchiveEvents(res.data?.events as Event[])
          } else {
            setArchiveEvents([...archiveEvents, ...(res.data?.events as Event[])])
          }

          setArchiveTotal(res.data?.count as number)
        }
      })
      .catch(() => {})
  }

  useEffect(() => {
    handleGetEvents()
    handleGetArchiveEvents()
  }, [initialized])

  const handleSearch = () => {
    handleGetEvents(true)
  }

  return (
    <Box className="main-body-with-footer" sx={{ pb: "250px !important" }}>
      <Title title={t("title")} sub_title={t("description")} />
      <Container maxWidth="lg" sx={{ p: 0 }}>
        <Box width="100%" sx={{ p: "20px" }}>
          <CheckBoxGroupField
            name={t("event_place") + "："}
            datas={areas}
            selected={areaID}
            handleSelected={setAreaID}
            labelSize={14}
            alignHorizontal={isPC}
          />
          <CheckBoxGroupField
            name={t("join_method") + "："}
            datas={[
              { label: t("online"), value: "ONLINE" },
              { label: t("offline"), value: "OFFLINE" },
            ]}
            selected={online}
            handleSelected={setOnline}
            alignHorizontal={isPC}
            sx={{ mt: "20px" }}
            labelSize={14}
          />
          <InputPeriodField
            name={t("event_date") + "："}
            fromDate={fromDate}
            toDate={toDate}
            handleFromDate={setFromDate}
            handleToDate={setToDate}
            alignHorizontal={isPC}
            sx={{ mt: "20px" }}
          />
          <CheckBoxGroupField
            name={t("category") + "："}
            selected={categories}
            handleSelected={setCategories}
            datas={eventCategories}
            alignHorizontal={isPC}
            sx={{ mt: "20px" }}
            labelSize={14}
          />
          <OutlinedButton
            caption={t("search")}
            sx={{ mt: "40px" }}
            onClick={() => handleSearch()}
          />
        </Box>
        {events.length === 0 ? (
          <Box sx={{ display: "flex", my: "50px", width: "100%" }}>
            <Typography.SubTitleNormal
              sx={{ mx: "auto", fontFamily: "Noto Sans JP !important", color: "gray" }}
            >
              {t("no_data")}
            </Typography.SubTitleNormal>
          </Box>
        ) : (
          <Box
            maxWidth="md"
            sx={{ mt: isPC ? "20px" : "20px", mb: "60px", mx: "auto", px: "10px" }}
          >
            <Grid container spacing={2}>
              {events.map((event, index) => {
                return (
                  <Grid item md={12} key={index}>
                    <EventItem item={event} handleSelected={handleItemSelected} clickable />
                  </Grid>
                )
              })}
            </Grid>

            {total !== events.length && (
              <OutlinedButton
                caption={t("more")}
                sx={{ mt: "60px", mx: "auto", display: "flex", justifyContent: "center" }}
                onClick={() => handleGetEvents()}
              />
            )}
          </Box>
        )}
      </Container>
      <Box
        sx={{
          width: "100%",
          bgcolor: "#222222",
          pt: isPC ? "60px" : "20px",
          pb: isPC ? "60px" : "20px",
        }}
      >
        <Container maxWidth="md">
          <Typography.Large color="white" sx={{ my: "20px" }}>
            {t("achive_list")}
          </Typography.Large>
          {archiveEvents.length === 0 ? (
            <Box sx={{ display: "flex", my: "50px", width: "100%" }}>
              <Typography.SubTitleNormal
                sx={{
                  mx: "auto",
                  color: "white",
                  fontFamily: "Noto Sans JP !important",
                }}
              >
                {t("no_data")}
              </Typography.SubTitleNormal>
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                {archiveEvents.map((event, index) => {
                  return (
                    <Grid item lg={4} key={index} md={4} sm={12} xs={12}>
                      <EventArchiveItem
                        item={event}
                        handleSelected={handleItemSelected}
                        handleItem={(item: Event) => {
                          let events = [...archiveEvents]
                          events[index] = item
                          setArchiveEvents(events)
                        }}
                      />
                    </Grid>
                  )
                })}
              </Grid>
              {archiveTotal !== archiveEvents.length && (
                <Button
                  variant="outlined"
                  sx={{
                    minWidth: "240px",
                    py: "16px",
                    px: "88px",
                    fontSize: "1rem",
                    fontFamily: "Noto Sans JP !important",
                    lineHeight: "1rem",
                    fontWeight: 500,
                    my: "60px",
                    mx: "auto",
                    display: "flex",
                    justifyContent: "center",
                    borderRadius: 0,
                    borderColor: "white",
                    bgcolor: "white",
                    color: "#222222",
                    "&:hover": { color: "white" },
                  }}
                  onClick={() => handleGetArchiveEvents()}
                >
                  {t("more")}
                </Button>
              )}
            </>
          )}
        </Container>
      </Box>
      <EventJoinModal
        open={openJoinModal}
        handleOpen={setOpenJoinModal}
        item={selectedItem}
        refreshEvents={() => handleGetEvents(true)}
      />
    </Box>
  )
}
